import { User } from 'lucide-react';
import { Form, FormField, FormLabel, FormInput, FormSelect, Button } from '../ui';

interface BookingFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  selectedProfessionalId: number | null;
  selectedServiceTypeId: number | null;
  selectedDate: string;
  selectedSlots: number[];
  selectedContactId: number | null;
  availableSlots: any[];
  professionals: any[];
  serviceTypes: any[];
  filteredServiceTypes: any[];
  contacts: any[];
  onProfessionalChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onServiceTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSlotToggle: (slotId: number) => void;
  onSelectContact: () => void;
  isLoading: boolean;
}

function BookingForm({
  onSubmit,
  selectedProfessionalId,
  selectedServiceTypeId,
  selectedDate,
  selectedSlots,
  selectedContactId,
  availableSlots,
  professionals,
  serviceTypes,
  filteredServiceTypes,
  contacts,
  onProfessionalChange,
  onServiceTypeChange,
  onDateChange,
  onSlotToggle,
  onSelectContact,
  isLoading,
}: BookingFormProps) {
  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="client_name" required>
          Nome do Cliente
        </FormLabel>
        <FormInput id="client_name" name="client_name" required placeholder="Nome do cliente" />
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="professional_id" required>
            Profissional
          </FormLabel>
          <FormSelect
            id="professional_id"
            name="professional_id"
            value={selectedProfessionalId?.toString() || ''}
            onChange={onProfessionalChange}
            required
          >
            <option value="">Selecione um profissional</option>
            {professionals?.map(professional => (
              <option key={professional.id} value={professional.id}>
                {professional.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="service_type_id" required>
            Tipo de Serviço
          </FormLabel>
          <FormSelect
            id="service_type_id"
            name="service_type_id"
            value={selectedServiceTypeId?.toString() || ''}
            onChange={onServiceTypeChange}
            required
            disabled={!selectedProfessionalId}
          >
            <option value="">Selecione um serviço</option>
            {filteredServiceTypes?.map(serviceType => (
              <option key={serviceType.id} value={serviceType.id}>
                {serviceType.name} ({serviceType.duration_minutes} min)
              </option>
            ))}
          </FormSelect>
          {!selectedProfessionalId && (
            <p className="mt-1 text-sm text-gray-500">Selecione um profissional primeiro</p>
          )}
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="date" required>
          Data
        </FormLabel>
        <FormInput
          type="date"
          id="date"
          name="date"
          value={selectedDate}
          onChange={onDateChange}
          required
        />
      </FormField>

      <FormField>
        <FormLabel required>Horários Disponíveis</FormLabel>
        {selectedProfessionalId ? (
          availableSlots && availableSlots.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
              {availableSlots.map(slot => (
                <div
                  key={slot.id}
                  className={`p-2 border rounded cursor-pointer text-center ${
                    selectedSlots.includes(slot.id)
                      ? 'bg-primary-100 border-primary-500 dark:bg-primary-900 dark:border-primary-500'
                      : 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600'
                  }`}
                  onClick={() => onSlotToggle(slot.id)}
                >
                  {slot.start_time} - {slot.end_time}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-gray-500">
                Nenhum horário disponível para esta data e profissional.
              </p>
            </div>
          )
        ) : (
          <div className="text-center p-4 border border-dashed rounded-md">
            <p className="text-gray-500">
              Selecione um profissional para ver os horários disponíveis.
            </p>
          </div>
        )}
        {selectedSlots.length > 0 && (
          <div className="mt-2 text-sm text-gray-600">
            {selectedSlots.length} horário(s) selecionado(s)
          </div>
        )}
      </FormField>

      <FormField>
        <FormLabel htmlFor="status" required>
          Status
        </FormLabel>
        <FormSelect id="status" name="status" required>
          <option value="booked">Reservado</option>
          <option value="confirmed">Confirmado</option>
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="contact">Contato</FormLabel>
        <div className="flex gap-2">
          <FormInput
            id="contact"
            name="contact"
            value={
              selectedContactId ? contacts?.find(c => c.id === selectedContactId)?.name || '' : ''
            }
            readOnly
            placeholder="Selecione um contato (opcional)"
            className="flex-1"
          />
          <Button type="button" variant="secondary" onClick={onSelectContact}>
            <User className="h-4 w-4 mr-2" />
            Selecionar
          </Button>
        </div>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button
          type="submit"
          isLoading={isLoading}
          className="sm:col-start-2"
          disabled={selectedSlots.length === 0}
        >
          Criar Agendamento
        </Button>
        <Button type="button" variant="secondary" className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default BookingForm;
